import {
  Active,
  DndContext,
  DragOverlay,
  MouseSensor,
  Over,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { arrayMove } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import { Col, Row } from "reactstrap";
import { isEmpty } from "underscore";

import useRecordAggregations from "../../api/api-hooks/records/useRecordAggregations";
import useRecordMetadata from "../../hooks/useRecordMetadata";
import FooterAggregation from "../Records/FooterAggregation";
import AppTableHeaders, { THeader } from "./AppTableHeaders";
import SkeletonLoader from "../../elements/SkeletonLoader";
import useReadableText from "../../hooks/useReadableText";
import useRecordImage from "../../hooks/useRecordImage";
import AppTablePagination from "./AppTablePagination";
import useFieldMap from "../../hooks/useFieldMap";
import AppTableFooters from "./AppTableFooters";
import useTableColumns from "./useTableColumns";
import ColumnAdd from "./ColumnAdd/ColumnAdd";
import useModule from "../../hooks/useModule";
import ColumnTrashCan from "./ColumnTrashCan";
import AppTableBody from "./AppTableBody";
import TableHeader from "./TableHeader";
import TableField from "./TableField";

const empty: [] = [];

const hiddenHeaders = ["id"];
const defaultHeaders = ["id", "_RecordName"];

// Atom storing Aggregation state - Only used when useAggregations param is true.
const aggregationAtom = atom({ data: null, loading: true });

// Component wrapping FooterAggregation so we can pass RecordListTable instance of aggregation atom.
const FooterComponent = ({
  field,
  onUpdateAggregation,
  aggregationData,
}: any) => {
  let { data, loading } = useAtomValue(aggregationAtom);

  if (aggregationData) loading = false;

  if (loading) return null;

  return (
    <FooterAggregation
      data={aggregationData ? aggregationData : data}
      loading={loading}
      field={field}
      onUpdateAggregation={onUpdateAggregation}
    />
  );
};

/**
 * AppTable - Table Component built for displaying GrowthCloud Records.
 * @param {string} moduleAPIName - The APIName of the module to display records for.
 * @param {object} query - The query object to search for records with.
 * @param {Array<string>?} hiddenFields - Fields to hide from the table (filtered out of displayFields)
 * @param {boolean} selectableRows - Whether or not the table's rows can be selected.
 * @param {boolean} columnAdd - Whether or not to add an action column to the table for adding columns
 * @param {function} onColumnAdd - Should only be passed if columnAdd is true. Callback function fired when column is added.
 * @param {function} onSort - Callback function for updating the OrderBy field in the query.
 * @param {object} selectedRecords - State from the parent component to store the Table's selected records.
 * @param {function} setSelectedRecords - State setter from parent component to update the selectedRecords
 * @param {boolean} useAggregations - Whether or not show aggregations in the footer of the table
 * @param {Array<object>} data - The data to be used inside of AppTable
 * @param {object} pagination - Pagination object used for pagination component.
 * @param {boolean} loading - Whether or not data is loading.
 * @param {any} error - Whether or not there was an error retrieving data.
 * @param {string} errorMsg - Error message to display when error exists.
 * @param {function} onOpenRecordName - Custom function to be called when RecordNameCell is clicked on in TableField.
 * @param {boolean} multiSubitemFieldAPIName - MultiSubitem field APIName, needed when using AppTable as MultiSubitem Table.
 * @param {object} parentRecord - Subitem Parent Record.
 * @param {object} selectedLayout - Current Layout.
 * @param {boolean} isDrawer - Current Layout.
 */
const AppTable = ({
  moduleAPIName,
  query,
  setQuery,
  selectableRows,
  columnAdd,
  columnAddMessage,
  onColumnAdd,
  onColumnRemove,
  onColumnReorder,
  onSort,
  onUpdateQuery,
  selectedRecords,
  setSelectedRecords,
  useAggregations,
  updateAggregation,
  data,
  pagination,
  loading,
  error,
  errorMsg,
  stretch,
  sticky = false,
  containerClasses = "",
  onOpenRecordName = null,
  separateName = false,
  multiSubitemFieldAPIName = "",
  parentRecord,
  paginationCSS,
  isDrawer = true,
  defaultRecordLayout,
  createMode = false,
  onChangeCreate,
  aggregationData = null,
  defaultHiddenHeaders = empty,
  draggingID,
  setDraggingID,
  dragDisabled,
  commentsWidget,
  isCustomTable = false,
  reusableViews,
  view,
  variableValues,
}: any) => {
  const fieldMap = useFieldMap(
    moduleAPIName,
    undefined,
    false,
    multiSubitemFieldAPIName
  );
  const fieldNameMap = useFieldMap(
    moduleAPIName,
    "Name",
    separateName,
    multiSubitemFieldAPIName
  );

  // @ts-ignore
  const { readableText } = useReadableText({
    query,
    moduleAPIName,
  });
  const { module } = useModule(moduleAPIName);
  const { imageField, imageFieldPresentation } = useRecordImage({ module });
  const { metadataField, metadataFieldType } = useRecordMetadata({ module });
  const setAggregationsAtom = useSetAtom(aggregationAtom);
  const [pinned, setPinned] = useState(false);
  const recordNameField = module?.Templates?.RecordListView?.RecordNameField;
  const recordNameEmpty = module?.Templates?.RecordListView?.RecordNameEmpty;

  const hiddenHeadersMemo = useMemo(
    () => [...defaultHiddenHeaders, ...hiddenHeaders],
    [defaultHiddenHeaders]
  );

  // Passed in function refs
  const onSortRef = useRef<any>();
  const onColumnAddRef = useRef<any>();
  const onColumnRemoveRef = useRef<any>();
  onSortRef.current = onSort;
  onColumnAddRef.current = onColumnAdd;
  onColumnRemoveRef.current = onColumnRemove;

  const fNameMapRef = useRef<Record<string, string>>();
  fNameMapRef.current = fieldNameMap;

  const headerComponent = useMemo(
    () => (item: any) =>
      (
        <TableHeader
          item={item}
          headerMapRef={fNameMapRef}
          orderBy={query?.OrderBy}
          onSort={onSortRef.current}
          onColumnRemove={onColumnRemoveRef.current}
          isCustomTable={isCustomTable}
        />
      ),
    [query?.OrderBy]
  );

  const tableDataComponent = useMemo(
    () => (item: any) => {
      return (
        <TableField
          // @ts-ignore
          fieldMap={fieldMap}
          item={item}
          onOpenRecordName={onOpenRecordName}
          imageField={imageField}
          imageFieldPresentation={imageFieldPresentation}
          subitemParentID={parentRecord?.id}
          metadataField={metadataField}
          metadataFieldType={metadataFieldType}
          parentRecord={parentRecord}
          isDrawer={isDrawer}
          defaultRecordLayout={defaultRecordLayout}
          createMode={createMode}
          onChangeCreate={onChangeCreate}
          commentsWidget={commentsWidget}
          recordNameField={recordNameField}
          recordNameEmpty={recordNameEmpty}
          reusableViews={reusableViews}
        />
      );
    },
    [
      onOpenRecordName,
      imageField,
      imageFieldPresentation,
      parentRecord,
      metadataField,
      metadataFieldType,
      fieldMap,
      defaultRecordLayout,
      hiddenHeadersMemo,
      recordNameField,
      recordNameEmpty,
    ]
  );

  const columnAddComponent = useMemo(
    () => (table: any) =>
      (
        <ColumnAdd
          table={table}
          moduleAPIName={moduleAPIName}
          hiddenFields={hiddenHeadersMemo}
          onColumnAdd={onColumnAddRef.current}
          description={columnAddMessage}
          multiSubitemFieldAPIName={multiSubitemFieldAPIName}
        />
      ),
    [
      moduleAPIName,
      columnAddMessage,
      multiSubitemFieldAPIName,
      hiddenHeadersMemo,
    ]
  );

  const { aggregations, aggregationsLoading } = useRecordAggregations({
    moduleAPIName: moduleAPIName,
    query: query,
    onSave: onUpdateQuery,
    disabled: !useAggregations,
    viewFilters: view?.ViewFilters,
    variableValues: variableValues,
  });

  const footerMemoComponent = useMemo(
    () => (column: any) => {
      if (Object.keys(fieldMap).length === 0) return null;

      const fieldAPIName = column.column.id;
      const field = fieldMap[fieldAPIName];

      const fieldObject = {
        APIName: fieldAPIName || null,
        FieldType: field?.FieldType || null,
        Name: field?.Name || null,
        CalculatedFieldData: field?.CalculatedFieldData,
      };

      return (
        <FooterComponent
          field={fieldObject}
          aggregationData={aggregationData}
          onUpdateAggregation={updateAggregation}
        />
      );
    },
    [updateAggregation, fieldMap, aggregationData]
  );

  const { columns } = useTableColumns({
    headers: query?.Fields ?? defaultHeaders,
    hiddenHeaders: hiddenHeadersMemo,
    headerComponent: headerComponent,
    tableDataComponent: tableDataComponent,
    selectableRows: selectableRows,
    columnAdd: columnAdd,
    columnAddComponent: columnAddComponent,
    ...((useAggregations || aggregationData) && {
      footerComponent: footerMemoComponent,
    }),
  });

  const table = useReactTable({
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    state: { ...(selectableRows && { rowSelection: selectedRecords ?? {} }) },
    ...(selectableRows && { onRowSelectionChange: setSelectedRecords }),
  });

  useEffect(() => {
    if (!useAggregations) return;

    setAggregationsAtom({ data: aggregations, loading: aggregationsLoading });
  }, [setAggregationsAtom, aggregations, aggregationsLoading, useAggregations]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require the pointer to move by 5 pixels before activating
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(MouseSensor, {
      // Require the mouse to move by 5 pixels before activating
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleScroll = (e: any) => {
    if (e?.target?.scrollLeft > 0) {
      if (!pinned) {
        setPinned(true);
      }
    } else {
      if (pinned) {
        setPinned(false);
      }
    }
  };

  const handleDragEnd = ({
    active,
    over,
  }: {
    active: Active;
    over: Over | null;
  }) => {
    setDraggingID && setDraggingID("");

    if (!active.id || !over?.id || !query?.Fields || active.id === over.id) {
      return;
    }

    if (over.id === "delete-column") {
      // @ts-ignore Support for removing lookup columns
      const reference = active.id?.includes(".") ? active.id.split(".")[0] : "";
      return onColumnRemove(active.id, reference);
    }

    const oldIndex = query.Fields.indexOf(active.id);
    const newIndex = query.Fields.indexOf(over.id);

    const newFields = arrayMove(query.Fields, oldIndex, newIndex);

    onColumnReorder && onColumnReorder({ ...query, Fields: newFields });
  };

  // Prev records is used for maintaining the table data when requerying is occuring (Ex: when onSort is called)
  if (!data && loading) {
    return (
      <div
        className="skeleton rounded"
        style={{ width: "100%", height: 250 }}
      ></div>
    );
  }

  const draggingHeader = draggingID
    ? table
        .getHeaderGroups()?.[0]
        ?.headers.find((h: any) => h?.column?.id === draggingID)
    : null;

  return (
    <>
      <div
        className={`gc-table-container ${containerClasses} ${
          selectableRows ? "gc-selectable-container" : ""
        } ${pinned ? "pinned" : ""}`}
        onScroll={(e) => handleScroll(e)}
      >
        <DndContext
          sensors={sensors}
          onDragStart={({ active }) => {
            setDraggingID && setDraggingID(active?.id as string);
          }}
          onDragEnd={handleDragEnd}
        >
          <table
            className={selectableRows ? "gc-selectable" : ""}
            style={stretch ? { minWidth: "100%" } : {}}
          >
            <AppTableHeaders
              items={query?.Fields || defaultHeaders}
              table={table}
              pinned={pinned}
              dragDisabled={dragDisabled}
            />
            <AppTableBody
              items={query?.Fields || defaultHeaders}
              table={table}
              loading={loading}
              error={error}
              selectable={selectableRows}
              deps={[tableDataComponent, columns]}
            />
            <AppTableFooters table={table} error={error} />
          </table>
          {createPortal(
            <DragOverlay modifiers={[snapCenterToCursor]} zIndex={99999}>
              {draggingHeader && (
                <THeader
                  isDragging={true}
                  isPlaceholder={draggingHeader?.isPlaceholder}
                  columnID={draggingID}
                  isMobile={false}
                  pinned={pinned}
                  renderValue={flexRender(
                    draggingHeader?.column?.columnDef?.header,
                    draggingHeader?.getContext()
                  )}
                  headerGroup={table.getHeaderGroups()?.[0]}
                  className="btn-oauth bg-ultralight rounded cursor-grabbing"
                />
              )}
            </DragOverlay>,
            document.body
          )}
          {draggingID && <ColumnTrashCan />}
        </DndContext>
        {error && (
          <div className="gc-table-error-container">
            <div className="gc-table-error">
              {errorMsg ??
                "There was an error performing your search. Please refresh and try again."}
            </div>
          </div>
        )}
        {!error && data?.length < 1 && (
          <div className="gc-table-error-container">
            <div className="gc-table-error pr-3" style={{ color: "unset" }}>
              {loading ? (
                <SkeletonLoader height={20} css="my-0" />
              ) : (
                "No records available based on your search."
              )}
            </div>
          </div>
        )}
        {/* Styles for gc-table-shadow not yet implemented. */}
        {pinned && <div className="gc-table-shadow"></div>}
      </div>
      {!isEmpty(pagination) || loading ? (
        <div
          className={`${sticky ? "gc-table-pagination-sticky" : ""}  ${
            containerClasses === "pr-4"
              ? "ml--3 ml-sm--4 pl-sm-2 text-center text-sm-left pr-0 mr-sm--4"
              : containerClasses
          }`}
        >
          <AppTablePagination
            records={data}
            pagination={pagination}
            query={query}
            setQuery={setQuery}
            error={error}
            setSelectedRecords={setSelectedRecords}
            loading={loading}
            module={module}
            containerCSS={paginationCSS}
          />
        </div>
      ) : query?.Limit?.Count > 0 ? (
        <div
          className={`${sticky ? "gc-table-pagination-sticky" : ""}  ${
            containerClasses === "pr-4"
              ? "ml--3 ml-sm--4 pl-sm-2 text-center text-sm-left pr-0 mr-sm--4"
              : containerClasses
          }`}
        >
          <div className={`px-3 py-2 py-sm-3 pr-4`}>
            <Row className="align-items-center">
              <Col xs="12" sm="">
                <div className="d-inline-block mb-3 mb-sm-0">
                  <small>
                    Top{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {query?.Limit?.Count}
                    </span>{" "}
                    records where {readableText}
                  </small>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AppTable;
